import React from 'react';
import { Inertia } from '@inertiajs/inertia'
import { useForm } from '@inertiajs/inertia-react'
import Layout from '../../Shared/Layout/LayoutAdmin';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Card from 'react-bootstrap/Card';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Breadcrumbs from '../../Shared/Components/Breadcrumbs';

function permissionsFromSubsection(subsection) {
    const base = subsection.permissions || []
    const fromGroups = (subsection.groups || []).flatMap((group) => group.permissions || [])
    return [...base, ...fromGroups]
}

function allPermissionsInCategory(category) {
    const base = category.permissions || []
    const fromSubsections = (category.subsections || []).flatMap((sub) => permissionsFromSubsection(sub))
    return [...base, ...fromSubsections]
}

function mapPermissionInList(list, permissionId, mapper) {
    return (list || []).map((p) => (p.id === permissionId ? mapper(p) : p))
}

function mapPermissionInCategory(category, permissionId, mapper) {
    return {
        ...category,
        permissions: mapPermissionInList(category.permissions, permissionId, mapper),
        subsections: (category.subsections || []).map((sub) => ({
            ...sub,
            permissions: mapPermissionInList(sub.permissions, permissionId, mapper),
            groups: (sub.groups || []).map((group) => ({
                ...group,
                permissions: mapPermissionInList(group.permissions, permissionId, mapper),
            })),
        })),
    }
}

function setAllInCategory(category, isChecked) {
    const setAll = (list) => (list || []).map((p) => ({ ...p, associated: isChecked }))

    return {
        ...category,
        permissions: setAll(category.permissions),
        subsections: (category.subsections || []).map((sub) => ({
            ...sub,
            permissions: setAll(sub.permissions),
            groups: (sub.groups || []).map((group) => ({
                ...group,
                permissions: setAll(group.permissions),
            })),
        })),
    }
}

const Permissions = ({ role, permissions, permissions_read_only: permissionsReadOnly = false }) => {
    const { data, setData, processing } = useForm({
        name: role.name || "",
        role_id: role.id,
        permissions: permissions || []
    });

    const handlePermissionChange = (categoryId, permissionId) => {
        const updatedPermissions = data.permissions.map((category) => {
            if (category.name !== categoryId) {
                return category
            }

            return mapPermissionInCategory(category, permissionId, (p) => ({
                ...p,
                associated: !p.associated,
            }))
        });

        setData('permissions', updatedPermissions);
    };

    const handleToggleAllPermissions = (categoryId, isChecked) => {
        const updatedPermissions = data.permissions.map((category) => {
            if (category.name !== categoryId) {
                return category
            }

            return setAllInCategory(category, isChecked)
        });

        setData('permissions', updatedPermissions);
    };

    function submit(e) {
        e.preventDefault();
        Inertia.post(route('roles.update.permissions', role.id), {
            name: data.name,
            role_id: data.role_id,
            permissions: data.permissions.map((category) => ({
                name: category.name,
                permissions: allPermissionsInCategory(category),
            })),
        });
    }

    const renderPermissionSwitch = (categoryName, permission) => (
        <Form.Check
            type="switch"
            key={`permission-${permission.id}`}
            id={`permission-${permission.id}`}
            label={permission.label}
            checked={permission.associated}
            disabled={permissionsReadOnly}
            onChange={() => handlePermissionChange(categoryName, permission.id)}
            className="mb-3"
        />
    );

    const renderSubsection = (categoryName, subsection) => {
        const groups = subsection.groups || []

        return (
            <div className="col-lg-4 col-md-6 mb-6" key={subsection.name}>
                <div className="fw-bold text-gray-800 mb-4 pb-2 border-bottom">
                    {subsection.name}
                </div>
                {(subsection.permissions || []).map((permission) =>
                    renderPermissionSwitch(categoryName, permission)
                )}
                {groups.map((group) => (
                    <div key={`${subsection.name}-${group.name}`} className="mt-5">
                        <div className="fw-semibold text-gray-700 fs-7 text-uppercase mb-3">
                            {group.name}
                        </div>
                        {(group.permissions || []).map((permission) =>
                            renderPermissionSwitch(categoryName, permission)
                        )}
                    </div>
                ))}
            </div>
        )
    }

    return (
        <Row>
            <div className="mb-7 mt-7">
                <Breadcrumbs currentPath="/admin/corporate_structure/roles/permissions" />
            </div>
            <h3 className="card-title align-items-start flex-column mb-3">
                <div className="card-label fw-bold text-gray-800">Permisos de rol de {data.name}</div>
                <div className="text-gray-400 mt-1 fw-semibold fs-6">
                    {permissionsReadOnly
                        ? 'El rol Admin tiene acceso total al sistema. Esta pantalla es solo informativa.'
                        : 'Selecciona los permisos para el rol seleccionado'}
                </div>
            </h3>
            <Form onSubmit={submit}>
                {data.permissions.map((category, catIndex) => (
                    <Card className="mb-3" key={catIndex}>
                        <Container className="pt-4 pb-4">
                            <Card.Title className="mt-5 mx-5 fs-6 text-gray-800">{category.name}</Card.Title>
                            <Card.Body className="px-0 py-0">
                                <div className="row mx-2">
                                    <Form.Group className="mt-5 mb-5 mx-3 col-12" controlId={`formAdd-${category.name}`}>
                                        <Form.Check
                                            type="switch"
                                            key={`all-${category.name}`}
                                            id={`all-${category.name}`}
                                            label="Todos"
                                            checked={allPermissionsInCategory(category).every((p) => p.associated)}
                                            disabled={permissionsReadOnly}
                                            className="mb-6"
                                            onChange={(e) => handleToggleAllPermissions(category.name, e.target.checked)}
                                        />

                                        {(category.is_sia_family || category.is_section_family) ? (
                                            <>
                                                {(category.permissions || []).map((permission) =>
                                                    renderPermissionSwitch(category.name, permission)
                                                )}
                                                <div className="row mt-2">
                                                    {(category.subsections || []).map((subsection) =>
                                                        renderSubsection(category.name, subsection)
                                                    )}
                                                </div>
                                            </>
                                        ) : (
                                            <div className="col-md-4 px-0">
                                                {(category.permissions || []).map((permission) =>
                                                    renderPermissionSwitch(category.name, permission)
                                                )}
                                            </div>
                                        )}
                                    </Form.Group>
                                </div>
                            </Card.Body>
                        </Container>
                    </Card>
                ))}
                {!permissionsReadOnly && (
                    <Button variant="primary" className='me-3' disabled={processing} type="submit">Guardar</Button>
                )}
                <Button onClick={() => window.history.back()} variant="secondary" type="button">Regresar</Button>
            </Form>
        </Row>
    );
}

Permissions.layout = page => <Layout children={page} title="Permisos" />

export default Permissions;
